import { CacheKeys, ErrorBoundary, useConfig, useSessionStorage } from '@bechtel/client-common';
import { Carousel, ShiftLog } from '@bechtel/client-dashboard-common';
import { WeldDrawerValuesProps } from '@bechtel/client-welds';
import { aboveBreakpoint, Box, Spinner, SpinnerSize, Theme, useMediaQuery } from '@bechtel/gds';
import { CommodityStats, CrewMember, PersonaType } from '@bechtel/types';
import { get } from 'lodash';
import { FC, Suspense, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ThemeContext } from 'styled-components';
import { CommodityStatsSection } from '~/commodity-stats';
import { MapMarkers } from '~/components/organisms/map-markers';
import { RodRequestList } from '~/components/organisms/rod-request-list/internals';
import { useWeeklyCommodityQueryResult, useWeldTypeId } from '~/hooks';
import { SearchBar } from '~/search-bar';
import { WeldFilterInput } from '~/search-bar/types';
import {
  CarouselContainerStyled,
  DashboardPersonaContentStyled,
  MapContainerStyled,
  WeldDashboardLeftSectionStyled,
  WeldDashboardRightSectionStyled,
  WeldSectionListStyled,
} from '~/styled';
import { WeldTabs } from '~/weld-tabs';
import { ForemanDashboardStatStyled, RodRequestListStyled } from './styled';

interface ForemanDashboardContentProps {
  crewMember: CrewMember;
  enableProjectMap?: boolean;
}
interface NavigateToWeldProps {
  weldDrawerValuesProps: WeldDrawerValuesProps;
  isWeeklyStats?: boolean;
  applyBlockedFilter?: string;
}
export const ForemanDashboardContent: FC<ForemanDashboardContentProps> = ({ crewMember, enableProjectMap }) => {
  const [showMap, setShowMap] = useState<boolean>(true);
  const [weldFilter, setWeldFilter] = useState<WeldFilterInput>();
  const navigate = useNavigate();
  const { breakpoints } = useContext<Theme>(ThemeContext);
  const isWideScreen = useMediaQuery(aboveBreakpoint(breakpoints.widescreen));
  const setCursor = useSessionStorage<number>(CacheKeys.WeldListCursor)[1];
  const setDrawerValues = useSessionStorage<WeldDrawerValuesProps>(CacheKeys.WeldDrawerValues)[1];
  const { features } = useConfig();
  const { weldTypeId } = useWeldTypeId();
  const { weeklyCommodityStatsError, weeklyCommodityStatsData, weeklyCommodityStatsLoading, weeklyRefetch } =
    useWeeklyCommodityQueryResult(weldTypeId, crewMember, weldFilter);
  const weeklyStatusStats: CommodityStats[] = get(
    weeklyCommodityStatsData?.commodityStats,
    'commodityStats.statusStats',
    []
  );
  const navigateToWeld = async (navigateToWeldProps: NavigateToWeldProps) => {
    setCursor(1);
    await setDrawerValues(navigateToWeldProps?.weldDrawerValuesProps);

    if (navigateToWeldProps?.isWeeklyStats || navigateToWeldProps?.applyBlockedFilter) {
      navigate('/', {
        state: {
          isWeeklyStats: navigateToWeldProps?.isWeeklyStats,
          applyBlockedFilter: navigateToWeldProps?.applyBlockedFilter,
        },
      });
    } else {
      navigate('/');
    }
  };
  const onSearch = (filter) => {
    setWeldFilter(filter);
  };

  return (
    <DashboardPersonaContentStyled key={crewMember?.id}>
      <ForemanDashboardStatStyled data-testid="StatBlock-container">
        <ErrorBoundary>
          <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
            <CommodityStatsSection
              personaType={PersonaType.FOREMAN}
              crewMember={crewMember}
              navigateToWeld={navigateToWeld}
              weldTypeId={weldTypeId}
              loading={weeklyCommodityStatsLoading}
              error={weeklyCommodityStatsError}
              refetch={weeklyRefetch}
              weeklyStatusStats={weeklyStatusStats}
            />
          </Suspense>
        </ErrorBoundary>
      </ForemanDashboardStatStyled>
      {isWideScreen ? (
        <WeldSectionListStyled key={crewMember?.id}>
          <WeldDashboardLeftSectionStyled>
            <SearchBar
              onSearch={onSearch}
              weldFilter={weldFilter}
              weldTypeId={weldTypeId}
              personaType={PersonaType.FOREMAN}
              crewMember={crewMember}
              showMap={showMap}
              toggleMap={setShowMap}
              enableProjectMap={enableProjectMap}
            />
            {showMap && enableProjectMap && (
              <MapContainerStyled key={crewMember?.id}>
                <MapMarkers weldFilter={weldFilter} crewMember={crewMember} />
              </MapContainerStyled>
            )}
            <WeldTabs
              personaType={PersonaType.FOREMAN}
              weldFilter={weldFilter}
              crewMember={crewMember}
              weldTypeId={weldTypeId}
            />
          </WeldDashboardLeftSectionStyled>
          <WeldDashboardRightSectionStyled key={crewMember?.id}>
            <RodRequestListStyled key={crewMember?.id}>
              <RodRequestList />
            </RodRequestListStyled>
            {features?.showShiftLog && (
              <Box mt={{ sm: '550' }}>
                <ShiftLog />
              </Box>
            )}
          </WeldDashboardRightSectionStyled>
        </WeldSectionListStyled>
      ) : (
        <Carousel key={crewMember?.id}>
          <CarouselContainerStyled>
            <SearchBar
              onSearch={onSearch}
              weldFilter={weldFilter}
              weldTypeId={weldTypeId}
              personaType={PersonaType.FOREMAN}
              crewMember={crewMember}
              showMap={showMap}
              toggleMap={setShowMap}
              enableProjectMap={enableProjectMap}
            />
            {showMap && enableProjectMap && (
              <MapContainerStyled style={{ height: '100%' }} key={crewMember?.id}>
                <MapMarkers weldFilter={weldFilter} crewMember={crewMember} />
              </MapContainerStyled>
            )}
          </CarouselContainerStyled>
          <CarouselContainerStyled>
            <SearchBar
              onSearch={onSearch}
              weldFilter={weldFilter}
              weldTypeId={weldTypeId}
              personaType={PersonaType.FOREMAN}
              crewMember={crewMember}
              showMap={showMap}
              toggleMap={setShowMap}
              enableProjectMap={enableProjectMap}
            />
            <WeldTabs
              personaType={PersonaType.FOREMAN}
              weldFilter={weldFilter}
              crewMember={crewMember}
              weldTypeId={weldTypeId}
            />
          </CarouselContainerStyled>
          <CarouselContainerStyled key={crewMember?.id}>
            <RodRequestList />
          </CarouselContainerStyled>
          {features?.showShiftLog && <ShiftLog />}
        </Carousel>
      )}
    </DashboardPersonaContentStyled>
  );
};
